/**
 * Branch Scope Helper
 *
 * 2 layers of branch filtering:
 * Layer 1 (Permission): user sees only branches they belong to (enforced)
 * Layer 2 (View Filter): user selects active branch in sidebar (optional)
 */

export interface BranchScopeConfig {
  type: "none" | "single" | "multi-field" | "inherit";
  fields?: string[];
  createField?: string;
}

export interface BranchScopeContext {
  /** User's allowed branch IDs from user_permissions. null = all branches (admin/owner) */
  userBranchIds: string[] | null;
  /** Active branch selected in sidebar. null = show all allowed branches */
  activeBranchId: string | null;
}

export interface BranchScopeCondition {
  field: string;
  operator: "eq" | "in";
  value: string | string[];
}

/**
 * Build branch scope WHERE conditions based on doctype config and user context.
 * Returns empty array if no filtering needed.
 */
export function buildBranchScopeConditions(
  config: BranchScopeConfig | undefined,
  context: BranchScopeContext,
): BranchScopeCondition[] {
  // Default to "none" if not specified
  if (!config || config.type === "none" || config.type === "inherit") {
    return [];
  }

  const fields = config.fields || ["branchId"];
  const conditions: BranchScopeCondition[] = [];

  // Layer 1: Permission (enforced) — user sees only their branches
  // Treat undefined the same as null (no restriction) to be safe if middleware didn't run
  if (Array.isArray(context.userBranchIds) && context.userBranchIds.length > 0) {
    if (config.type === "single") {
      conditions.push({
        field: fields[0],
        operator: "in",
        value: context.userBranchIds,
      });
    } else if (config.type === "multi-field") {
      // For multi-field, we need OR logic across fields
      // This is handled specially — each field IN userBranchIds, combined with OR
      // We represent this as multiple "in" conditions that should be OR-ed
      for (const field of fields) {
        conditions.push({
          field,
          operator: "in",
          value: context.userBranchIds,
        });
      }
    }
  }

  // Layer 2: View Filter (optional) — sidebar selection narrows further
  if (context.activeBranchId) {
    if (config.type === "single") {
      // If permission layer already applied, this AND further narrows
      // Replace the "in" with "eq" since it's more restrictive
      // But only if activeBranchId is within userBranchIds
      conditions.push({
        field: fields[0],
        operator: "eq",
        value: context.activeBranchId,
      });
    } else if (config.type === "multi-field") {
      for (const field of fields) {
        conditions.push({
          field,
          operator: "eq",
          value: context.activeBranchId,
        });
      }
    }
  }

  return conditions;
}

/**
 * Get the branch field to auto-fill when creating a new document.
 * Returns null if doctype doesn't need branch auto-fill.
 */
export function getCreateBranchField(
  config: BranchScopeConfig | undefined,
): string | null {
  if (!config || config.type === "none" || config.type === "inherit") {
    return null;
  }
  return config.createField || null;
}

/**
 * Check if a record is within the user's branch scope.
 * Used for single-record access validation (GET/:id, PATCH/:id, DELETE/:id)
 */
export function isRecordInBranchScope(
  record: Record<string, unknown>,
  config: BranchScopeConfig | undefined,
  context: BranchScopeContext,
): boolean {
  if (!config || config.type === "none" || config.type === "inherit") {
    return true; // No branch filtering
  }

  // Admin/Owner bypass — also treat undefined (middleware skipped) as bypass
  if (!Array.isArray(context.userBranchIds)) {
    return true;
  }

  const fields = config.fields || ["branchId"];

  if (config.type === "single") {
    const recordBranch = record[fields[0]] as string | null;
    if (!recordBranch) return true; // No branch set on record = accessible
    return context.userBranchIds.includes(recordBranch);
  }

  if (config.type === "multi-field") {
    // At least one branch field must match
    return fields.some((field) => {
      const recordBranch = record[field] as string | null;
      if (!recordBranch) return true;
      return context.userBranchIds!.includes(recordBranch);
    });
  }

  return true;
}
